import * as React from 'react';
import { MdOpenInNew } from 'react-icons/md';
import { FaLongArrowAltRight } from 'react-icons/fa';
import Link from 'components/Link';
import { FlatButton } from 'styles/elements';
import TickerOverviewTable from 'screens/TickerResearchPage/TickerOverviewTable';
import BarChartSectionGrid from '../BarChartSectionGrid';
import OptionsFlowTable from './OptionsFlowTable';
import { OptionsFlowSectionProps } from './types';

export const filterTradesForLatestDate = <T extends { date?: string | null }>(
  allTrades: T[],
  tradesToFilter: T[] = allTrades,
): T[] => {
  const latestDate = allTrades.reduce<string | null>((maxDate, trade) => {
    if (!trade.date) return maxDate;
    return !maxDate || trade.date > maxDate ? trade.date : maxDate;
  }, null);

  if (!latestDate) return tradesToFilter;
  return tradesToFilter.filter((trade) => trade.date === latestDate);
};

const OptionsFlowSection: React.FC<OptionsFlowSectionProps> = ({
  ticker,
  trades,
  sweepTrades,
  unusualTrades,
  putFlow,
  dailyVolume,
  loading,
  viewer,
  metricMode = 'total',
}) => {
  const latestTrades = React.useMemo(() => filterTradesForLatestDate(trades), [trades]);
  const latestSweepTrades = React.useMemo(
    () => filterTradesForLatestDate(trades, sweepTrades),
    [trades, sweepTrades],
  );
  const latestUnusualTrades = React.useMemo(
    () => filterTradesForLatestDate(trades, unusualTrades),
    [trades, unusualTrades],
  );

  return (
    <section className="mb-8 md:mb-12">
      <div
        id="section-options-flow"
        className="mb-3 flex flex-col gap-2 md:mb-4 md:flex-row md:items-end"
      >
        <h2 className="mb-1 mr-2 text-2xl font-semibold md:text-3xl">{ticker} Options Flow</h2>
        <div className="hidden md:block">
          <Link href={`/ticker-flow/${ticker}`} isExternal>
            <FlatButton className="mt-1">
              Open Ticker Research <MdOpenInNew />
            </FlatButton>
          </Link>
        </div>
      </div>
      <div className="mb-4">
        <TickerOverviewTable
          viewer={viewer}
          trades={latestTrades}
          sweepTrades={latestSweepTrades}
          unusualTrades={latestUnusualTrades}
          activeTicker={ticker}
          putFlow={Math.round(putFlow * 100)}
          dailyVolume={dailyVolume}
          metricMode={metricMode}
        />
      </div>
      <div className="overflow-hidden rounded">
        <OptionsFlowTable trades={trades} loading={loading} />
      </div>
      {trades && trades.length > 0 && (
        <div className="mt-2 text-sm text-gray-400">
          10 most recent trades |{' '}
          <Link href={`/ticker-flow/${ticker}`} className="hover:text-gray-300">
            View all trades <FaLongArrowAltRight className="ml-2 inline" />
          </Link>
        </div>
      )}
      <div id="section-options-flow-trends" className="mt-6">
        <BarChartSectionGrid trades={trades} activeTicker={ticker} />
      </div>
    </section>
  );
};

export default OptionsFlowSection;
